import Exifr from 'exifr';
import type { Editor } from '@tiptap/core';
import { generateJSON } from '@tiptap/core';
import type { JSONContent } from '@tiptap/core';
import { Docx, exportDocx } from '@tiptap-pro/extension-export-docx';
import type { CustomNode, ExportDocxWarning } from '@tiptap-pro/extension-export-docx';
import type { Doc as YDoc } from 'yjs';

import {
  type DocumentFooterSettings,
  type DocumentHeaderSettings,
  type DocumentSettings,
  DocumentViewMode,
  readDocumentSettings,
  toPagesCustomFormat,
} from 'containers/Tiptap/components/tiptap-extension/document-settings-yjs';
import { createTableSpacer, editorStyleOverrides, editorTableCellMargins, toDocxHex } from 'containers/Tiptap/lib/export-styles';
import { headerFooterExtensions } from 'containers/Tiptap/lib/header-footer-extensions';
import { DocumentVariables, resolveDocumentTokens } from 'containers/Tiptap/lib/header-footer-variables';
import API from 'utils/api';
import { isProxyUrl } from 'containers/Tiptap/lib/tiptap-utils';
import { resolvePaletteColor } from 'containers/Tiptap/lib/palette-colors';
import { EMPTY_CELL_WIDTH, RESIZE_MIN_WIDTH } from 'containers/Tiptap/components/tiptap-node/table-node/lib/tiptap-table-utils';
import { numberingFormats } from 'Tiptap/editor-config/numbering-formats';
import Logger from 'utils/logger';

// `Docx` is the exporter's own namespace for Paragraph, Table, Header, etc.
const {
  AlignmentType,
  BorderStyle,
  Footer,
  Header,
  ImageRun,
  PageNumber,
  Paragraph,
  ShadingType,
  Tab,
  Table,
  TableCell,
  TableLayoutType,
  TableRow,
  TabStopType,
  TextRun,
  WidthType,
} = Docx;

type Paragraph = Docx.Paragraph;
type TextRun = Docx.TextRun;
type ImageRun = Docx.ImageRun;
type Header = Docx.Header;
type Footer = Docx.Footer;
type Table = Docx.Table;
type TableCell = Docx.TableCell;
type TableRow = Docx.TableRow;

export type ExportFormat = 'pdf' | 'docx';

// --- Page geometry ---
//
// The exporter derives page setup from the Pages layout only when export runs
// through its editor command. We call the standalone `exportDocx()`, which has
// no editor, so page size and margins have to be passed in — otherwise every
// document exports as A4 with the exporter's own 3.17cm sides, whatever the
// author picked. Content width then drives the table and image caps.

export interface PageGeometry {
  widthPx: number;
  heightPx: number;
  marginsPx: { top: number; right: number; bottom: number; left: number };
  headerOffsetPx: number;
  footerOffsetPx: number;
  contentWidthPx: number;
}

const cmToPx = (cm: number): number => Math.round((cm / 2.54) * 96);

// Matches the exporter's documented defaults, used for pageless documents where
// there is no page to honour.
const DEFAULT_PAGE_GEOMETRY = buildPageGeometry({
  widthPx: cmToPx(21),
  heightPx: cmToPx(29.7),
  marginsPx: { top: cmToPx(2.54), right: cmToPx(3.17), bottom: cmToPx(2.54), left: cmToPx(3.17) },
  headerOffsetPx: cmToPx(1.25),
  footerOffsetPx: cmToPx(1.25),
});

function buildPageGeometry(geometry: Omit<PageGeometry, 'contentWidthPx'>): PageGeometry {
  const contentWidthPx = geometry.widthPx - geometry.marginsPx.left - geometry.marginsPx.right;
  return { ...geometry, contentWidthPx: Math.max(1, contentWidthPx) };
}

// The Pages sheet is `box-sizing: border-box` with a 1px border, so its text column is
// 2px narrower than the margins imply — 600px on A4, not 602px. Height is unaffected:
// Pages derives that from the format numbers, never from the bordered box.
const SHEET_BORDER_PX = 1;

export function pageGeometry(settings: DocumentSettings | null): PageGeometry {
  if (!settings || settings.viewMode === DocumentViewMode.Pageless) return DEFAULT_PAGE_GEOMETRY;
  const format = toPagesCustomFormat(settings);
  return buildPageGeometry({
    widthPx: format.width,
    heightPx: format.height,
    marginsPx: {
      ...format.margins,
      left: format.margins.left + SHEET_BORDER_PX,
      right: format.margins.right + SHEET_BORDER_PX,
    },
    headerOffsetPx: settings.header?.topMarginPx ?? DEFAULT_PAGE_GEOMETRY.headerOffsetPx,
    footerOffsetPx: settings.footer?.bottomMarginPx ?? DEFAULT_PAGE_GEOMETRY.footerOffsetPx,
  });
}

// Points, not px: the exporter documents px but types its measures as cm/in/mm/pc/pi/pt.
// At 96dpi 1px is exactly 0.75pt, so nothing is lost.
type Points = `${number}pt`;

const pxToPt = (px: number): Points => `${px * 0.75}pt`;

export function docxPageOptions(geometry: PageGeometry): {
  pageSize: { width: Points; height: Points };
  pageMargins: { top: Points; right: Points; bottom: Points; left: Points; header: Points; footer: Points };
} {
  const { widthPx, heightPx, marginsPx, headerOffsetPx, footerOffsetPx } = geometry;
  return {
    pageSize: { width: pxToPt(widthPx), height: pxToPt(heightPx) },
    pageMargins: {
      top: pxToPt(marginsPx.top),
      right: pxToPt(marginsPx.right),
      bottom: pxToPt(marginsPx.bottom),
      left: pxToPt(marginsPx.left),
      // Word measures both from the page edge, so an offset past the body margin
      // would let the header run into the body text.
      header: pxToPt(Math.min(headerOffsetPx, marginsPx.top)),
      footer: pxToPt(Math.min(footerOffsetPx, marginsPx.bottom)),
    },
  };
}

// --- Image normalisation: fix EXIF orientation + convert unsupported formats ---

const SUPPORTED_CONTENT_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/bmp', 'image/svg+xml'];

async function srcToBlob(src: string): Promise<Blob> {
  const response = await fetch(src);
  return response.blob();
}

async function resolveProxyUrl(src: string): Promise<string> {
  const response = await API.common.resolveFileUrl(src)();
  return response.data.url;
}

// Applies EXIF orientation correction and converts unsupported formats to JPEG.
// Uses exifr to read orientation explicitly and imageOrientation:'none' to get raw
// pixels, avoiding inconsistent browser behaviour around EXIF in canvas drawing.
async function normalizeImage(src: string): Promise<string> {
  const blob = await srcToBlob(src);

  const exif = await Exifr.parse(blob, ['Orientation']).catch(() => null);
  const orientation: number = exif?.Orientation ?? 1;

  const mimeType = blob.type || '';
  const isSupported = SUPPORTED_CONTENT_TYPES.some((t) => mimeType.includes(t));

  // SVG and GIF: skip — canvas would rasterize/lose animation
  if (mimeType.includes('image/svg+xml') || mimeType.includes('image/gif')) {
    return src;
  }

  // No correction needed and format is already supported
  if (orientation === 1 && isSupported) {
    return src;
  }

  const bitmap = await createImageBitmap(blob, { imageOrientation: 'none' });
  const { width: w, height: h } = bitmap;

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close();
    throw new Error('Failed to get canvas context');
  }

  const needsSwap = [5, 6, 7, 8].includes(orientation);
  canvas.width = needsSwap ? h : w;
  canvas.height = needsSwap ? w : h;

  switch (orientation) {
    case 2: ctx.transform(-1, 0, 0, 1, canvas.width, 0); break;
    case 3: ctx.transform(-1, 0, 0, -1, canvas.width, canvas.height); break;
    case 4: ctx.transform(1, 0, 0, -1, 0, canvas.height); break;
    case 5: ctx.transform(0, 1, 1, 0, 0, 0); break;
    case 6: ctx.transform(0, 1, -1, 0, canvas.height, 0); break;
    case 7: ctx.transform(0, -1, -1, 0, canvas.height, canvas.width); break;
    case 8: ctx.transform(0, -1, 1, 0, 0, canvas.width); break;
  }

  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();
  return canvas.toDataURL('image/jpeg', 0.92);
}

async function normalizeImages(doc: JSONContent, maxImageWidthPx: number): Promise<JSONContent> {
  if (doc.type === 'image' && doc.attrs?.src) {
    let src = doc.attrs.src as string;

    // Dead blob URLs (from failed uploads) can never be fetched — remove them from export
    if (src.startsWith('blob:')) {
      return { ...doc, type: 'paragraph', attrs: undefined, content: [] };
    }

    // Proxy URLs need to be resolved to real presigned S3 URLs before exportDocx can fetch them
    if (isProxyUrl(src)) {
      try {
        src = await resolveProxyUrl(src);
      } catch {
        return { ...doc, type: 'paragraph', attrs: undefined, content: [] };
      }
    }

    try {
      const normalized = await normalizeImage(src);
      src = normalized;
    } catch {
      return { ...doc, type: 'paragraph', attrs: undefined, content: [] };
    }

    // Cap to the page's content width — prevents overflow for both resized and natural-size images
    const currentWidth = doc.attrs.width ? Number(doc.attrs.width) : null;
    const cappedWidth = currentWidth !== null ? Math.min(currentWidth, maxImageWidthPx) : maxImageWidthPx;
    const widthChanged = cappedWidth !== currentWidth;

    if (src !== doc.attrs.src || widthChanged) {
      return {
        ...doc,
        attrs: { ...doc.attrs, src, ...(widthChanged && cappedWidth !== null && { width: cappedWidth }) },
      };
    }
  }

  if (doc.content) {
    const convertedContent = await Promise.all(doc.content.map((child) => normalizeImages(child, maxImageWidthPx)));
    return { ...doc, content: convertedContent };
  }

  return doc;
}

// --- Header/Footer → docx (rich: text marks, alignment, page tokens, images) ---
//
// Content is stored as HTML; parse to Tiptap JSON (overlay schema), normalise
// images, then build docx Header/Footer ourselves — the library's
// convertHeader/Footer only take the first child and don't map {page}/{total}.

const PAGE_TOKEN_REGEX = /(\{page\}|\{total\})/g;

const ALIGNMENT: Record<string, (typeof AlignmentType)[keyof typeof AlignmentType]> = {
  left: AlignmentType.LEFT,
  center: AlignmentType.CENTER,
  right: AlignmentType.RIGHT,
  justify: AlignmentType.JUSTIFIED,
};

const MIME_TO_DOCX_TYPE: Record<string, 'png' | 'jpg' | 'gif' | 'bmp'> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/gif': 'gif',
  'image/bmp': 'bmp',
};

// Keep header/footer logos a sensible size regardless of their on-page width,
// without letting the cap exceed the content width on a narrow page.
const MAX_HEADER_FOOTER_IMAGE_WIDTH = 300;

const headerFooterImageWidth = (geometry: PageGeometry): number =>
  Math.min(MAX_HEADER_FOOTER_IMAGE_WIDTH, geometry.contentWidthPx);

type RunMark = { type?: string; attrs?: Record<string, unknown> };

type RunMarkProps = {
  bold?: boolean;
  italics?: boolean;
  underline?: Record<string, never>;
  strike?: boolean;
  color?: string;
  shading?: { type: typeof ShadingType.CLEAR; fill: string; color: 'auto' };
};

const shadingFor = (fill: string): NonNullable<RunMarkProps['shading']> => ({
  type: ShadingType.CLEAR,
  fill,
  color: 'auto',
});

function marksToRunProps(marks?: RunMark[]): RunMarkProps {
  const props: RunMarkProps = {};
  for (const mark of marks ?? []) {
    if (mark.type === 'bold') props.bold = true;
    else if (mark.type === 'italic') props.italics = true;
    else if (mark.type === 'underline') props.underline = {};
    else if (mark.type === 'strike') props.strike = true;
    else if (mark.type === 'textStyle') {
      const color = toDocxHex(mark.attrs?.color);
      if (color) props.color = color;
      const fill = toDocxHex(mark.attrs?.backgroundColor);
      if (fill) props.shading = shadingFor(fill);
    } else if (mark.type === 'highlight') {
      const fill = toDocxHex(mark.attrs?.color);
      if (fill) props.shading = shadingFor(fill);
    }
  }
  return props;
}

// {page} / {total} → docx PageNumber fields so Word renders real page numbers;
// literal text keeps its inline marks (bold/italic/underline/strike).
function textToRuns(text: string, marks?: RunMark[]): TextRun[] {
  const props = marksToRunProps(marks);
  return text
    .split(PAGE_TOKEN_REGEX)
    .filter((part) => part.length > 0)
    .map((part) => {
      if (part === '{page}') return new TextRun({ ...props, children: [PageNumber.CURRENT] });
      if (part === '{total}') return new TextRun({ ...props, children: [PageNumber.TOTAL_PAGES] });
      return new TextRun({ ...props, text: part });
    });
}

async function imageToRun(node: JSONContent, maxWidthPx: number): Promise<ImageRun | null> {
  const src = node.attrs?.src;
  if (typeof src !== 'string' || !src || src.startsWith('blob:')) return null;
  try {
    const blob = await srcToBlob(src);
    const docxType = MIME_TO_DOCX_TYPE[blob.type];
    // SVG/unknown aren't supported as header/footer images in export (would need a
    // raster fallback); skip rather than fail the whole export.
    if (!docxType) return null;
    const data = await blob.arrayBuffer();

    let width = node.attrs?.width ? Number(node.attrs.width) : null;
    let height = node.attrs?.height ? Number(node.attrs.height) : null;
    if (!width || !height) {
      const bitmap = await createImageBitmap(blob);
      const ratio = bitmap.height / bitmap.width;
      width = width ?? bitmap.width;
      height = height ?? Math.round(width * ratio);
      bitmap.close();
    }
    if (width > maxWidthPx) {
      height = Math.round((height / width) * maxWidthPx);
      width = maxWidthPx;
    }
    return new ImageRun({ data, type: docxType, transformation: { width, height } });
  } catch {
    return null;
  }
}

async function paragraphRuns(node: JSONContent, maxImageWidthPx: number): Promise<(TextRun | ImageRun)[]> {
  const children: (TextRun | ImageRun)[] = [];
  for (const child of node.content ?? []) {
    if (child.type === 'text' && child.text) {
      children.push(...textToRuns(child.text, child.marks as RunMark[] | undefined));
    } else if (child.type === 'image') {
      const run = await imageToRun(child, maxImageWidthPx);
      if (run) children.push(run);
    } else if (child.type === 'hardBreak') {
      children.push(new TextRun({ break: 1 }));
    }
  }
  return children;
}

async function paragraphFromNode(node: JSONContent, maxImageWidthPx: number): Promise<Paragraph> {
  const children = await paragraphRuns(node, maxImageWidthPx);
  const alignment = typeof node.attrs?.textAlign === 'string' ? ALIGNMENT[node.attrs.textAlign] : undefined;
  return new Paragraph({ children, ...(alignment ? { alignment } : {}) });
}

// Pages flattens tables in headers/footers, stacking the zones; a right tab stop keeps left/right on one line.
async function zonesToTabbedParagraph(
  table: JSONContent,
  maxImageWidthPx: number,
  contentWidthPx: number,
): Promise<Paragraph | null> {
  const rows = table.content?.filter((row) => row.type === 'tableRow') ?? [];
  const cells = rows.length === 1 ? (rows[0].content ?? []) : [];
  if (cells.length !== 2 || cells.some((cell) => cell.attrs?.background)) return null;
  const paragraphs = cells.map((cell) => cell.content ?? []);
  if (paragraphs.some((blocks) => blocks.length !== 1 || blocks[0].type !== 'paragraph')) return null;
  const [left, right] = await Promise.all(paragraphs.map(([node]) => paragraphRuns(node, maxImageWidthPx)));
  return new Paragraph({
    children: [...left, new TextRun({ children: [new Tab()] }), ...right],
    tabStops: [{ type: TabStopType.RIGHT, position: dxaWidth(contentWidthPx).size }],
  });
}

const NONE_BORDER = { style: BorderStyle.NONE, size: 0, color: 'auto' } as const;
const BORDERLESS_TABLE = {
  top: NONE_BORDER,
  bottom: NONE_BORDER,
  left: NONE_BORDER,
  right: NONE_BORDER,
  insideHorizontal: NONE_BORDER,
  insideVertical: NONE_BORDER,
};
const BORDERLESS_CELL = { top: NONE_BORDER, bottom: NONE_BORDER, left: NONE_BORDER, right: NONE_BORDER };

// Tiptap stores column widths in px; Word cell widths are in twips (1px = 15 twips at 96dpi).
const PX_TO_TWIPS = 15;

const dxaWidth = (px: number) => ({ size: Math.round(px * PX_TO_TWIPS), type: WidthType.DXA });

function cellWidth(node: JSONContent, evenSplitPx?: number) {
  const colwidth = node.attrs?.colwidth;
  if (Array.isArray(colwidth) && typeof colwidth[0] === 'number') return dxaWidth(colwidth[0]);
  return evenSplitPx ? dxaWidth(evenSplitPx) : undefined;
}

export async function tableFromNode(node: JSONContent, maxImageWidthPx: number): Promise<Table> {
  const rows: TableRow[] = [];
  const tableWidthPx = typeof node.attrs?.width === 'number' ? node.attrs.width : null;
  const firstRow = node.content?.find((row) => row.type === 'tableRow');
  const firstRowCells = firstRow?.content?.length ?? 0;
  const evenSplitPx = tableWidthPx && firstRowCells ? Math.floor(tableWidthPx / firstRowCells) : undefined;
  for (const rowNode of node.content ?? []) {
    if (rowNode.type !== 'tableRow') continue;
    const cells: TableCell[] = [];
    for (const cellNode of rowNode.content ?? []) {
      if (cellNode.type !== 'tableCell' && cellNode.type !== 'tableHeader') continue;
      const children = await blocksToDocxChildren(cellNode.content ?? [], maxImageWidthPx);
      if (children[children.length - 1] instanceof Table) {
        children.push(createTableSpacer());
      }
      const width = cellWidth(cellNode, evenSplitPx);
      const fill = toDocxHex(cellNode.attrs?.background);
      cells.push(
        new TableCell({
          // docx requires at least one block child per cell.
          children: children.length ? children : [new Paragraph({})],
          borders: BORDERLESS_CELL,
          ...(width ? { width } : {}),
          ...(fill ? { shading: shadingFor(fill) } : {}),
          ...(typeof cellNode.attrs?.colspan === 'number' ? { columnSpan: cellNode.attrs.colspan } : {}),
        }),
      );
    }
    rows.push(new TableRow({ children: cells }));
  }
  const columnWidths = (firstRow?.content ?? []).map((cell) => cellWidth(cell, evenSplitPx)?.size ?? 0);
  const hasGrid = columnWidths.length > 0 && columnWidths.every((width) => width > 0);
  return new Table({
    rows,
    width: tableWidthPx ? dxaWidth(tableWidthPx) : { size: 100, type: WidthType.PERCENTAGE },
    borders: BORDERLESS_TABLE,
    ...(hasGrid ? { columnWidths, layout: TableLayoutType.FIXED } : {}),
  });
}

export async function blocksToDocxChildren(
  content: JSONContent[],
  maxImageWidthPx: number,
): Promise<(Paragraph | Table)[]> {
  const children: (Paragraph | Table)[] = [];
  for (const node of content) {
    if (node.type === 'paragraph' || node.type === 'heading') {
      children.push(await paragraphFromNode(node, maxImageWidthPx));
    } else if (node.type === 'image') {
      const run = await imageToRun(node, maxImageWidthPx);
      const align = node.attrs?.['data-align'];
      const alignment = typeof align === 'string' ? ALIGNMENT[align] : undefined;
      if (run) children.push(new Paragraph({ children: [run], ...(alignment ? { alignment } : {}) }));
    } else if (node.type === 'table') {
      if (children[children.length - 1] instanceof Table) children.push(createTableSpacer());
      children.push(await tableFromNode(node, maxImageWidthPx));
    }
  }
  return children;
}

const escapeHtml = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

function substituteDocVariables(html: string, variables?: DocumentVariables): string {
  if (!variables) return html;
  return resolveDocumentTokens(html, {
    documentName: escapeHtml(variables.documentName),
    documentVersion: escapeHtml(variables.documentVersion),
  });
}

async function contentToDocxChildren(
  html: string,
  geometry: PageGeometry,
  variables?: DocumentVariables,
): Promise<(Paragraph | Table)[]> {
  if (!html || !html.trim()) return [];
  const maxImageWidthPx = headerFooterImageWidth(geometry);
  const parsed = generateJSON(substituteDocVariables(html, variables), headerFooterExtensions) as JSONContent;
  const normalized = (await normalizeImages(parsed, maxImageWidthPx)) as JSONContent;
  const json = setTableWidths(resolveExportColors(normalized), geometry.contentWidthPx);
  const blocks: (Paragraph | Table)[] = [];
  for (const node of json.content ?? []) {
    const tabbed = node.type === 'table' ? await zonesToTabbedParagraph(node, maxImageWidthPx, geometry.contentWidthPx) : null;
    blocks.push(...(tabbed ? [tabbed] : await blocksToDocxChildren([node], maxImageWidthPx)));
  }
  return blocks;
}

type DocxHeaders = { default?: Header; first?: Header; differentFirstPage?: boolean };
type DocxFooters = { default?: Footer; first?: Footer; differentFirstPage?: boolean };

export async function buildDocxHeaders(
  settings: DocumentHeaderSettings | null,
  geometry: PageGeometry,
  variables?: DocumentVariables,
): Promise<DocxHeaders | undefined> {
  if (!settings?.show) return undefined;
  const headers: DocxHeaders = {
    default: new Header({ children: await contentToDocxChildren(settings.content, geometry, variables) }),
  };
  if (settings.differentFirstPage) {
    // first-page variant starts empty when not explicitly set — same as Word/Google Docs behaviour
    headers.first = new Header({
      children: await contentToDocxChildren(settings.variants?.first ?? '', geometry, variables),
    });
    headers.differentFirstPage = true;
  }
  return headers;
}

export async function buildDocxFooters(
  settings: DocumentFooterSettings | null,
  geometry: PageGeometry,
  variables?: DocumentVariables,
): Promise<DocxFooters | undefined> {
  if (!settings?.show) return undefined;
  const footers: DocxFooters = {
    default: new Footer({ children: await contentToDocxChildren(settings.content, geometry, variables) }),
  };
  if (settings.differentFirstPage) {
    footers.first = new Footer({
      children: await contentToDocxChildren(settings.variants?.first ?? '', geometry, variables),
    });
    footers.differentFirstPage = true;
  }
  return footers;
}

// Pulls ydoc off the collaboration extension so callers don't have to plumb it through.
function getYDocFromEditor(editor: Editor): YDoc | undefined {
  const ext = editor.extensionManager.extensions.find((e) => e.name === 'collaboration');
  const doc = (ext?.options as { document?: unknown } | undefined)?.document;
  return doc as YDoc | undefined;
}

// --- Export functions ---

const TIPTAP_CONVERT_ENDPOINT = 'https://api.tiptap.dev/v2/convert';

// --- Table of contents → linked heading list ---

type TocHeading = { level: number; text: string; anchor: string };

function nodeText(node: JSONContent): string {
  if (node.type === 'text') return node.text ?? '';
  return (node.content ?? []).map(nodeText).join('');
}

function bookmarkHeadings(node: JSONContent, headings: TocHeading[]): JSONContent {
  // The taskList export flattens its children to text, so a bookmark inside one would be lost.
  if (node.type === 'taskList') return node;
  if (node.type === 'heading') {
    const text = nodeText(node).trim();
    if (!text) return node;
    // A leading underscore marks a Word bookmark hidden, and Google Docs and Pages drop hidden ones on import.
    const anchor = `toc${headings.length + 1}`;
    headings.push({ level: node.attrs?.level ?? 1, text, anchor });
    return { ...node, content: [{ type: 'bookmark', attrs: { id: anchor } }, ...(node.content ?? [])] };
  }
  if (!node.content) return node;
  return { ...node, content: node.content.map((child) => bookmarkHeadings(child, headings)) };
}

function tocParagraphs(headings: TocHeading[], showTitle: boolean): JSONContent[] {
  const paragraphs: JSONContent[] = [];
  if (showTitle) {
    paragraphs.push({
      type: 'paragraph',
      content: [{ type: 'text', marks: [{ type: 'bold' }], text: 'Table of contents' }],
    });
  }
  headings.forEach((h) => {
    const indent = '    '.repeat(Math.max(0, h.level - 1));
    const entry: JSONContent = {
      type: 'text',
      marks: [{ type: 'link', attrs: { href: `#${h.anchor}` } }],
      text: h.text,
    };
    paragraphs.push({ type: 'paragraph', content: indent ? [{ type: 'text', text: indent }, entry] : [entry] });
  });
  return paragraphs;
}

function replaceTocNodes(node: JSONContent, headings: TocHeading[]): JSONContent[] {
  if (node.type === 'tocNode') return tocParagraphs(headings, node.attrs?.showTitle !== false);
  if (!node.content) return [node];
  return [{ ...node, content: node.content.flatMap((child) => replaceTocNodes(child, headings)) }];
}

const hasToc = (node: JSONContent): boolean => node.type === 'tocNode' || !!node.content?.some(hasToc);

export function expandTocNodes(doc: JSONContent): JSONContent {
  if (!hasToc(doc)) return doc;
  const headings: TocHeading[] = [];
  const bookmarked = bookmarkHeadings(doc, headings);
  return { ...bookmarked, content: (bookmarked.content ?? []).flatMap((child) => replaceTocNodes(child, headings)) };
}

// --- Table borders ---

// Same line the editor draws: 1px (6 eighths of a point) of the table border
// grey, flattened onto white because OOXML borders have no alpha. The exporter
// writes every edge as `none` otherwise, so the table renders invisible.
const TABLE_BORDER = { style: BorderStyle.SINGLE, size: 6, color: 'D5D6D7' };

const tableBorders = {
  top: TABLE_BORDER,
  bottom: TABLE_BORDER,
  left: TABLE_BORDER,
  right: TABLE_BORDER,
  insideHorizontal: TABLE_BORDER,
  insideVertical: TABLE_BORDER,
};

// --- Table column widths ---

// A numeric width keeps the exporter on its fixed path, which honours colwidths.
// The fluid path derives an even split from page width, flattening dragged columns.

const isColumnWidth = (width: unknown): width is number => typeof width === 'number' && width > 0;

function cellStartColumns(rows: JSONContent[]): number[][] {
  const rowsLeft: number[] = [];
  return rows.map((row) => {
    const blocked = rowsLeft.map((left) => left > 0);
    for (let column = 0; column < rowsLeft.length; column++) rowsLeft[column] = Math.max(0, rowsLeft[column] - 1);
    let column = 0;
    return (row.content ?? []).map((cell) => {
      while (blocked[column]) column++;
      const start = column;
      const colspan = Number(cell.attrs?.colspan ?? 1);
      const rowspan = Number(cell.attrs?.rowspan ?? 1);
      for (let offset = 0; offset < colspan; offset++) rowsLeft[start + offset] = rowspan - 1;
      column += colspan;
      return start;
    });
  });
}

// The exporter drops every colwidth unless all cells carry one, and resizing only
// writes the dragged columns. An undragged column renders as a 1fr track, so it
// gets an even share of the width the others leave free.
function fillColumnWidths(table: JSONContent, availableWidthPx: number, minColumnWidthPx: number): JSONContent | null {
  const rows = table.content?.filter((child) => child.type === 'tableRow') ?? [];
  const starts = cellStartColumns(rows);
  const widths: (number | null)[] = [];
  rows.forEach((row, rowIndex) =>
    (row.content ?? []).forEach((cell, cellIndex) => {
      const colspan = Number(cell.attrs?.colspan ?? 1);
      for (let offset = 0; offset < colspan; offset++) {
        const column = starts[rowIndex][cellIndex] + offset;
        const width = cell.attrs?.colwidth?.[offset];
        widths[column] = widths[column] ?? (isColumnWidth(width) ? width : null);
      }
    }),
  );

  const known = widths.filter(isColumnWidth);
  if (known.length === 0) return null;
  const missing = widths.length - known.length;
  const freeWidth = availableWidthPx - known.reduce((sum, width) => sum + width, 0);
  const fill = missing > 0 ? Math.max(minColumnWidthPx, Math.floor(freeWidth / missing)) : 0;
  const filled = Array.from(widths, (width) => width ?? fill);
  const totalWidth = filled.reduce((sum, width) => sum + width, 0);
  const scale = totalWidth > availableWidthPx ? availableWidthPx / totalWidth : 1;
  const resolved = filled.map((width) => Math.floor(width * scale));

  return {
    ...table,
    content: table.content?.map((child) => {
      const rowIndex = rows.indexOf(child);
      if (rowIndex === -1) return child;
      return {
        ...child,
        content: child.content?.map((cell, cellIndex) => {
          const start = starts[rowIndex][cellIndex];
          const colspan = Number(cell.attrs?.colspan ?? 1);
          return { ...cell, attrs: { ...cell.attrs, colwidth: resolved.slice(start, start + colspan) } };
        }),
      };
    }),
    attrs: {
      ...table.attrs,
      width: typeof table.attrs?.width === 'number' ? table.attrs.width : resolved.reduce((sum, width) => sum + width, 0),
    },
  };
}

// Pages lays undragged columns out as shrinkable grid tracks; pageless keeps the table's 120px cell minimum.
export function setTableWidths(node: JSONContent, contentWidthPx: number, isPageless = false): JSONContent {
  const content = node.content?.map((child) => setTableWidths(child, contentWidthPx, isPageless));
  const withContent = content ? { ...node, content } : node;
  if (node.type !== 'table') return withContent;

  const explicitWidth = typeof node.attrs?.width === 'number' ? node.attrs.width : null;
  const minColumnWidthPx = isPageless ? EMPTY_CELL_WIDTH : RESIZE_MIN_WIDTH;
  const filled = fillColumnWidths(withContent, explicitWidth ?? contentWidthPx, minColumnWidthPx);
  if (filled) return filled;
  return explicitWidth === null ? { ...withContent, attrs: { ...node.attrs, width: contentWidthPx } } : withContent;
}

type JSONMark = NonNullable<JSONContent['marks']>[number];

const TABLE_CELL_TYPES = new Set(['tableCell', 'tableHeader']);
const TABLE_RENDERING_TYPES = new Set(['table']);

const rendersAsTable = (node: JSONContent | undefined): boolean => !!node?.type && TABLE_RENDERING_TYPES.has(node.type);

export function separateTables(node: JSONContent): JSONContent {
  if (!node.content) return node;
  const content: JSONContent[] = [];
  for (const child of node.content) {
    const separated = separateTables(child);
    if (rendersAsTable(separated) && rendersAsTable(content[content.length - 1])) {
      content.push({ type: 'tableSpacer' });
    }
    content.push(separated);
  }
  if (node.type && TABLE_CELL_TYPES.has(node.type) && rendersAsTable(content[content.length - 1])) {
    content.push({ type: 'tableSpacer' });
  }
  return { ...node, content };
}

function resolveMarkColors(mark: JSONMark): JSONMark[] {
  if (!mark.attrs) return [mark];
  if (mark.type === 'highlight') {
    if (typeof mark.attrs.color !== 'string') return [mark];
    const color = resolvePaletteColor(mark.attrs.color);
    return color === null ? [] : [{ ...mark, attrs: { ...mark.attrs, color } }];
  }
  if (mark.type !== 'textStyle') return [mark];

  const attrs = { ...mark.attrs };
  for (const key of ['color', 'backgroundColor']) {
    const value = attrs[key];
    if (typeof value !== 'string') continue;
    const color = resolvePaletteColor(value);
    if (color === null) delete attrs[key];
    else attrs[key] = color;
  }
  return [{ ...mark, attrs }];
}

export function resolveExportColors(node: JSONContent): JSONContent {
  const resolved: JSONContent = { ...node };
  if (node.content) resolved.content = node.content.map(resolveExportColors);
  if (node.marks) resolved.marks = node.marks.flatMap(resolveMarkColors);

  const backgroundColor = node.attrs?.backgroundColor;
  if (node.type && TABLE_CELL_TYPES.has(node.type) && typeof backgroundColor === 'string') {
    const { backgroundColor: _, ...attrs } = node.attrs ?? {};
    // The exporter shades cells from ConvertKit's `background`; the colour picker writes `backgroundColor`.
    resolved.attrs = { ...attrs, background: resolvePaletteColor(backgroundColor) };
  }
  return resolved;
}

async function exportAsDocx(editor: Editor, customNodes: CustomNode[], variables?: DocumentVariables): Promise<Blob> {
  const ydoc = getYDocFromEditor(editor);
  const settings = ydoc ? readDocumentSettings(ydoc) : null;
  const geometry = pageGeometry(settings);

  const json = editor.getJSON();
  const normalized = await normalizeImages(json, geometry.contentWidthPx);
  const isPageless = !settings || settings.viewMode === DocumentViewMode.Pageless;
  const safeJson = separateTables(
    setTableWidths(expandTocNodes(resolveExportColors(normalized)), geometry.contentWidthPx, isPageless),
  );

  const headers = isPageless ? undefined : await buildDocxHeaders(settings.header ?? null, geometry, variables);
  const footers = isPageless ? undefined : await buildDocxFooters(settings.footer ?? null, geometry, variables);

  const editorNumberingFormats = editor.storage.orderedListNumbering.numberingFormats as unknown as typeof numberingFormats;

  const result = await exportDocx({
    document: safeJson,
    exportType: 'blob',
    styleOverrides: editorStyleOverrides,
    customNodes,
    numberingFormats: editorNumberingFormats,
    tableOverrides: { borders: tableBorders, margins: editorTableCellMargins },
    onWarning: (warning: ExportDocxWarning) =>
      Logger.warn(`DOCX export skipped an image: ${warning.message}`, warning),
    ...docxPageOptions(geometry),
    ...(headers ? { headers } : {}),
    ...(footers ? { footers } : {}),
  });
  return result as Blob;
}

async function exportAsPdf(
  editor: Editor,
  customNodes: CustomNode[],
  convertToken: string,
  variables?: DocumentVariables,
): Promise<Blob> {
  const docxBlob = await exportAsDocx(editor, customNodes, variables);

  const formData = new FormData();
  formData.append(
    'docxFile',
    new File([docxBlob], 'document.docx', {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    }),
  );

  const response = await fetch(`${TIPTAP_CONVERT_ENDPOINT}/export/pdf`, {
    method: 'POST',
    body: formData,
    headers: {
      Authorization: `Bearer ${convertToken}`,
      'X-Tiptap-Extension': 'export-pdf',
    },
  });

  if (!response.ok) {
    throw new Error(`PDF export failed: ${response.statusText}`);
  }

  return response.blob();
}

export async function safeExport(
  editor: Editor,
  format: ExportFormat,
  customNodes: CustomNode[],
  convertToken: string,
  variables?: DocumentVariables,
): Promise<Blob> {
  return format === 'pdf'
    ? exportAsPdf(editor, customNodes, convertToken, variables)
    : exportAsDocx(editor, customNodes, variables);
}

// --- Download helpers ---

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 100);
}
